<script setup></script>

<template>
  <section id="approach" class="sections-approach">
    <div class="sections-approach__container container">
      <div class="sections-approach__body">
        <CSpriteIcon
          v-for="item in 12"
          :key="item"
          class="sections-approach__icon sections-approach__icon--plus"
          :class="`sections-approach__icon--plus--${item}`"
          name="plus"
        />

        <UiCaptionUnderline
          caption="approach"
          class="sections-approach__caption"
        />

        <h2 class="sections-approach__title">
          <span class="sections-approach__font sections-approach__font--title">
            Development as a living journey
          </span>
        </h2>

        <p class="sections-approach__description">
          <span
            class="sections-approach__font sections-approach__font--description"
          >
            Working with an iterative approach allows us to get feedback quickly
            and make changes to the technical requirements, even during
            development
          </span>
        </p>

        <div class="sections-approach__approaches-grid">
          <p class="sections-approach__approach-item">
            <span
              class="sections-approach__font sections-approach__font--approach-item"
            >
              At Centour.io, we see product development as a living journey —
              not a fixed destination. In the world of travel, logistics, and
              digital platforms, change isn’t the exception — it’s the rhythm.
            </span>
          </p>

          <p class="sections-approach__approach-item">
            <span
              class="sections-approach__font sections-approach__font--approach-item"
            >
              That’s why we build with flexibility in mind from day one. Our
              systems are designed like modular routes — where each component
              can evolve, shift, or even be replaced as your goals shift and
              markets move.
            </span>
          </p>

          <p class="sections-approach__approach-item">
            <span
              class="sections-approach__font sections-approach__font--approach-item"
            >
              Our iterative development process allows us to respond quickly to
              new requirements, user feedback, or strategic pivots. As the
              product grows, so does our involvement — continuously improving,
              refining, and optimizing what we've built.
            </span>
          </p>

          <p class="sections-approach__approach-item">
            <span
              class="sections-approach__font sections-approach__font--approach-item"
            >
              Especially for startups and scaling businesses, the ability to
              adapt is often the difference between momentum and stagnation.
            </span>
          </p>

          <p class="sections-approach__approach-item">
            <span
              class="sections-approach__font sections-approach__font--approach-item"
            >
              We don’t resist change — we expect it, and we plan for it at the
              architectural level.
            </span>
          </p>

          <p class="sections-approach__approach-item">
            <span
              class="sections-approach__font sections-approach__font--approach-item"
            >
              The road ahead might change, but your infrastructure will be ready
              for it.
            </span>
          </p>
        </div>

        <div class="sections-approach__approaches-column">
          <div class="sections-approach__approaches-row">
            <p class="sections-approach__approach-item">
              <span
                class="sections-approach__font sections-approach__font--approach-item"
              >
                At Centour.io, we see product development as a living journey —
                not a fixed destination. In the world of travel, logistics, and
                digital platforms, change isn’t the exception — it’s the rhythm.
              </span>
            </p>

            <p class="sections-approach__approach-item">
              <span
                class="sections-approach__font sections-approach__font--approach-item"
              >
                Especially for startups and scaling businesses, the ability to
                adapt is often the difference between momentum and stagnation.
              </span>
            </p>
          </div>

          <div class="sections-approach__approaches-row">
            <p class="sections-approach__approach-item">
              <span
                class="sections-approach__font sections-approach__font--approach-item"
              >
                That’s why we build with flexibility in mind from day one. Our
                systems are designed like modular routes — where each component
                can evolve, shift, or even be replaced as your goals shift and
                markets move.
              </span>
            </p>

            <p class="sections-approach__approach-item">
              <span
                class="sections-approach__font sections-approach__font--approach-item"
              >
                We don’t resist change — we expect it, and we plan for it at the
                architectural level.
              </span>
            </p>
          </div>

          <div class="sections-approach__approaches-row">
            <p class="sections-approach__approach-item">
              <span
                class="sections-approach__font sections-approach__font--approach-item"
              >
                Our iterative development process allows us to respond quickly
                to new requirements, user feedback, or strategic pivots. As the
                product grows, so does our involvement — continuously improving,
                refining, and optimizing what we've built.
              </span>
            </p>

            <p class="sections-approach__approach-item">
              <span
                class="sections-approach__font sections-approach__font--approach-item"
              >
                The road ahead might change, but your infrastructure will be
                ready for it.
              </span>
            </p>
          </div>
        </div>

        <div class="sections-approach__footer">
          <span class="sections-approach__font sections-approach__font--footer">
            We’re not just building for launch.<br />
            We’re building for
            <span class="sections-approach__white">what's next.</span>
          </span>
        </div>

        <img
          src="/icons/centourio_route.svg"
          alt="approach"
          class="sections-approach__icon sections-approach__icon--approach"
        />
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
.sections-approach {
  position: relative;
  overflow: hidden;
  background: url('#{$images-path}/red_map_background.webp') center / cover
    no-repeat;

  @supports not (-webkit-touch-callout: none) {
    background-attachment: fixed;
  }

  &__font {
    display: block;

    &--title {
      font-size: em(30);
      font-weight: 800;
      line-height: 100%;

      @include media-breakpoint-down(md) {
        font-size: em(24);
        line-height: 115%;
      }

      @include media-breakpoint-down(sm) {
        font-size: em(20);
      }
    }

    &--description {
      font-family: $font-family-second;
      font-size: em(22);
      font-weight: 500;
      line-height: 115%;

      @include media-breakpoint-down(md) {
        font-size: em(17);
        letter-spacing: -0.02em;
      }

      @include media-breakpoint-down(sm) {
        font-size: em(16);
      }
    }

    &--approach-item {
      font-family: $font-family-second;
      font-size: em(16);
      font-weight: 500;
      line-height: 118%;

      @include media-breakpoint-down(md) {
        font-size: em(13);
      }

      @include media-breakpoint-down(sm) {
        font-size: em(14);
      }
    }

    &--footer {
      font-size: em(28);
      font-weight: 800;
      line-height: 115%;

      @include media-breakpoint-down(md) {
        font-size: em(20);
      }

      @include media-breakpoint-down(sm) {
        font-size: em(16);
      }
    }
  }

  &__icon {
    &--approach {
      position: absolute;
      bottom: em(233);
      left: em(-12);
      width: em(1145);
      height: em(131);

      @include media-breakpoint-down(md) {
        bottom: em(140);
        left: em(-234);
        width: em(916);
        height: em(104);
      }

      @include media-breakpoint-down(sm) {
        bottom: em(90);
        left: em(-4);
        width: calc(100% + #{em(8)});
        height: em(104);
      }
    }

    &--plus {
      position: absolute;
      width: em(40);
      height: em(40);
      pointer-events: none;
      opacity: 0.5;

      @include media-breakpoint-down(sm) {
        width: em(30);
        height: em(30);
      }

      &--1 {
        top: em(0);
        left: em(0);

        @include media-breakpoint-down(md) {
          top: em(-3);
          left: em(-4);
        }

        @include media-breakpoint-down(sm) {
          top: em(-2);
          left: em(-6);
        }
      }

      &--2 {
        top: em(60);
        left: em(0);

        @include media-breakpoint-down(md) {
          top: em(45);
          left: em(-4);
        }

        @include media-breakpoint-down(sm) {
          top: em(44);
          left: em(-6);
        }
      }

      &--3 {
        top: em(138);
        left: em(0);

        @include media-breakpoint-down(md) {
          top: em(135);
          left: em(-4);
        }

        @include media-breakpoint-down(sm) {
          top: em(118);
          left: em(-6);
        }
      }

      &--4 {
        top: em(256);
        left: em(0);

        @include media-breakpoint-down(md) {
          top: em(230);
          left: em(-4);
        }

        @include media-breakpoint-down(sm) {
          top: em(219);
          left: em(-6);
        }
      }

      &--5 {
        top: em(586);
        left: em(0);

        @include media-breakpoint-down(md) {
          top: unset;
          bottom: em(80);
          left: em(-4);
        }

        @include media-breakpoint-down(sm) {
          top: unset;
          bottom: em(65);
          left: em(-6);
        }
      }

      &--6 {
        top: em(694);
        left: em(0);

        @include media-breakpoint-down(md) {
          top: unset;
          bottom: em(-5);
          left: em(-4);
        }

        @include media-breakpoint-down(sm) {
          top: unset;
          bottom: 0;
          left: em(-6);
        }
      }

      &--7 {
        top: em(0);
        left: em(560);

        @include media-breakpoint-down(md) {
          top: em(-3);
          right: em(-4);
          left: unset;
        }

        @include media-breakpoint-down(sm) {
          top: em(-2);
          right: em(-6);
        }
      }

      &--8 {
        top: em(60);
        left: em(560);

        @include media-breakpoint-down(md) {
          top: em(45);
          right: em(-4);
          left: unset;
        }

        @include media-breakpoint-down(sm) {
          top: em(44);
          right: em(-6);
        }
      }

      &--9 {
        top: em(138);
        left: em(560);

        @include media-breakpoint-down(md) {
          top: em(135);
          right: em(-4);
          left: unset;
        }

        @include media-breakpoint-down(sm) {
          top: em(118);
          right: em(-6);
        }
      }

      &--10 {
        top: em(256);
        left: em(560);

        @include media-breakpoint-down(md) {
          top: em(230);
          right: em(-4);
          left: unset;
        }

        @include media-breakpoint-down(sm) {
          top: em(219);
          right: em(-6);
        }
      }

      &--11 {
        top: em(586);
        left: em(560);

        @include media-breakpoint-down(md) {
          top: unset;
          right: em(-4);
          bottom: em(80);
          left: unset;
        }

        @include media-breakpoint-down(sm) {
          top: unset;
          right: em(-6);
          bottom: em(65);
        }
      }

      &--12 {
        top: em(694);
        left: em(560);

        @include media-breakpoint-down(md) {
          top: unset;
          right: em(-4);
          bottom: em(-5);
          left: unset;
        }

        @include media-breakpoint-down(sm) {
          top: unset;
          right: em(-6);
          bottom: em(0);
        }
      }
    }
  }

  &__container {
  }

  &__body {
    position: relative;
    padding: em(40);

    @include media-breakpoint-down(md) {
      padding: em(34) em(33);
    }

    @include media-breakpoint-down(sm) {
      padding: em(28) em(21);
    }
  }

  &__caption {
  }

  &__title {
    margin-top: em(45);
    color: $text-dark-on-red;

    @include media-breakpoint-down(md) {
      margin-top: em(35);
    }

    @include media-breakpoint-down(sm) {
      max-width: em(307);
      margin-top: em(27);
    }
  }

  &__description {
    width: em(520);
    margin-top: em(45);
    color: $text-dark-on-red;

    @include media-breakpoint-down(md) {
      width: 100%;
      margin-top: em(35);
    }

    @include media-breakpoint-down(sm) {
      max-width: em(307);
      margin-top: em(28);
    }
  }

  &__approaches-grid {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    grid-gap: em(80);
    margin-top: em(52);

    @include media-breakpoint-down(md) {
      display: none;
    }
  }

  &__approaches-column {
    display: flex;
    flex-direction: column;
    gap: em(24) 0;
    margin-top: em(42);

    @include media-breakpoint-up(md) {
      display: none;
    }

    @include media-breakpoint-down(sm) {
      gap: em(22) 0;
      margin-top: em(35);
    }
  }

  &__approaches-row {
    display: flex;
    flex-direction: column;
    gap: em(7) 0;
  }

  &__approach-item {
    color: $color-white;
  }

  &__footer {
    margin-top: em(52);
    color: $text-dark-on-red;

    @include media-breakpoint-down(md) {
      margin: em(168) em(-3) 0;
    }

    @include media-breakpoint-down(sm) {
      margin: em(138) 0 0;
    }
  }

  &__white {
    color: $color-white;
  }
}
</style>
